import React, { FC, useEffect, useMemo, useState } from "react";
import { usePromise } from "react-use";

import { useAppContext } from "../../hooks/useAppContext";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { ClickableWarning, ProductValidation } from "./product-validation";

import { labels } from "./labels";

type WarningData = eXpress.core.ValidationMessages & ClickableWarning;

export type IProductValidationContainer = {
	messages: eXpress.core.ValidationMessages[];
	/**
	 * Executed when warning is clicked, this could be used to close the container (like a dialog)
	 * Use a stable function
	 */
	onClick(): void;
	showDetails: boolean;
};

export const ProductValidationContainer: FC<IProductValidationContainer> = ({
	messages,
	onClick,
	showDetails,
}) => {
	const { localizedContent, editor, googleAnalytics, client } = useAppContext();
	const resolveIfMounted = usePromise();

	const { createErrorHandler } = useErrorHandling();

	const [warningItems, setWarningItems] = useState<WarningData[] | undefined>();

	const warningItemsPromises: Promise<Partial<WarningData> | undefined>[] = useMemo(() => {
		const setCurrentZoneAndBroadcastAndLogGa = (zone: eXpress.core.IZone | undefined) => {
			editor.selection.setCurrentZoneAndBroadcast(zone);
			googleAnalytics.trackEventToGa({
				trackingAction: "ProdValMessageClicked",
			});
		};

		const setCurrentSpreadAndBroadcastAndLogGa = (spread: eXpress.core.Spread | undefined) => {
			editor.selection.setCurrentSpreadAndBroadCast(spread);
			googleAnalytics.trackEventToGa({
				trackingAction: "ProdValMessageClicked",
			});
		};

		return messages.map(async (validateMessage) => {
			switch (validateMessage.name) {
				case "DefaultTextInTextZones": {
					return localizedContent
						.getKeyAsync(labels.defaultTextInTextZones)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
							onClick: () => {
								const zone = validateMessage.details.affectedZones.first();
								setCurrentZoneAndBroadcastAndLogGa(zone);
							},
						}));
				}
				case "NotAllTextZonesRequired": {
					return localizedContent
						.getKeyAsync(labels.notAllTextZonesRequired)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
							onClick: () => {
								const zone = validateMessage.details.affectedZones.first();
								setCurrentZoneAndBroadcastAndLogGa(zone);
							},
						}));
				}
				case "NotMinimumTextZonesFilled": {
					return localizedContent
						.getKeyAsync(labels.notMinimumTextZonesFilled)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.numberOfRequiredZones.toString(),
								(
									validateMessage.details.totalNumberOfFillableZones -
									validateMessage.details.affectedZones.length
								).toString(),
								validateMessage.details.totalNumberOfFillableZones.toString(),
							),
							onClick: () => {
								const zone = validateMessage.details.affectedZones.first();
								setCurrentZoneAndBroadcastAndLogGa(zone);
							},
						}));
				}
				case "NotMinimumPhotoZonesFilled":
					return localizedContent
						.getKeyAsync(labels.notMinimumPhotoZonesFilled)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.numberOfRequiredZones.toString(),
								(
									validateMessage.details.totalNumberOfFillableZones -
									validateMessage.details.affectedZones.length
								).toString(),
								validateMessage.details.totalNumberOfFillableZones.toString(),
							),
							onClick: () => {
								setCurrentZoneAndBroadcastAndLogGa(
									validateMessage.details.affectedZones.first(),
								);
							},
						}));
				case "BadQualityTextZones": {
					return localizedContent
						.getKeyAsync(labels.badQualityTextZones)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
							onClick: () => {
								setCurrentZoneAndBroadcastAndLogGa(
									validateMessage.details.affectedZones.first(),
								);
							},
						}));
				}
				case "TextOutsideBounds": {
					let newMessagePromise = Promise.resolve(validateMessage);

					const fixableByUserZones = validateMessage.details.affectedItems.filter(
						({ canBeFixedByUser }) => canBeFixedByUser,
					);
					const nonFixableByUserZones = validateMessage.details.affectedItems.filter(
						({ canBeFixedByUser }) => !canBeFixedByUser,
					);

					// Don't show 0 incorrect textzones
					if (fixableByUserZones.length <= 0) {
						return Promise.resolve(undefined); // Remove message
					}

					fixableByUserZones.forEach((affectedItem) => {
						const { zone } = affectedItem;
						newMessagePromise = newMessagePromise
							.then(() => localizedContent.getKeyAsync(labels.textOutsideBounds))
							.then((label) => ({
								...validateMessage,
								message: eXpress.utils.formatString(
									label,
									fixableByUserZones.length.toString(),
								),
								onClick: () => {
									if (zone.canSelect() && zone.canChangeContent()) {
										setCurrentZoneAndBroadcastAndLogGa(zone);
									} else {
										setCurrentSpreadAndBroadcastAndLogGa(zone.spread);
									}
								},
							}));
					});

					// Log Error to server if there are non fixable errors
					if (eXpress.utils.isNonEmptyArray(nonFixableByUserZones)) {
						const items = nonFixableByUserZones.map(
							// eslint-disable-next-line @typescript-eslint/no-unnecessary-template-expression
							(zone) => `${zone.zone?.toString()}`,
						);
						const firstText = nonFixableByUserZones.first().zone.text;
						createErrorHandler()(
							new eXpress.Warning(
								"TopHeaderBar.getValidationPopupInfo",
								`
							Following zones have text that doesn't fit: ${items.join(", ")}. Text of first zone: '${firstText}'.
							`,
							),
						);
					}
					return newMessagePromise;
				}
				case "NoQuantity":
					return localizedContent
						.getKeyAsync(labels.noQuantity)
						.then((label: string) => ({
							...validateMessage,
							message: label,
						}));
				case "OldStartDate":
					return localizedContent
						.getKeyAsync(labels.oldStartDate)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.monthName,
							),
						}));
				case "AllPhotoZonesRequired":
					return localizedContent
						.getKeyAsync(labels.allPhotoZonesRequired)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
							onClick: () => {
								setCurrentZoneAndBroadcastAndLogGa(
									validateMessage.details.affectedZones.first(),
								);
							},
						}));
				case "AllPhotoZonesOnCoverRequired":
					return localizedContent
						.getKeyAsync(labels.notAllPhotoZonesOnCoverFilled)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
						}));
				case "RejectedPhotosNotAllowed":
					return localizedContent
						.getKeyAsync(labels.rejectedPhotosNotAllowed)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
							onClick: () => {
								setCurrentZoneAndBroadcastAndLogGa(
									validateMessage.details.affectedZones.first(),
								);
							},
						}));
				case "EmptyPhotoZones":
					return localizedContent
						.getKeyAsync(labels.emptyPhotoZones)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
							onClick: () => {
								setCurrentZoneAndBroadcastAndLogGa(
									validateMessage.details.affectedZones.first(),
								);
							},
						}));
				case "BadQualityPhotoZones":
					return localizedContent
						.getKeyAsync(
							showDetails
								? labels.badQualityPhotoZonesExtraInfo
								: labels.badQualityPhotoZones,
						)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								validateMessage.details.affectedZones.length.toString(),
							),
							onClick: () => {
								setCurrentZoneAndBroadcastAndLogGa(
									validateMessage.details.affectedZones.first(),
								);
							},
						}));
				case "SplitBookOnPageExceeded": {
					const { splitBookOnPage, amountOfBooks } = validateMessage.details;
					return localizedContent
						.getKeyAsync(labels.splitBookOnPageExceeded)
						.then((label: string) => ({
							...validateMessage,
							message: eXpress.utils.formatString(
								label,
								splitBookOnPage.toString(),
								amountOfBooks.toString(),
							),
						}));
				}
				case "AutoSizeValidation": {
					const warningMessage = `${
						validateMessage.details.affectedItems.length
					} text zones don't have a correct auto size, first: ${validateMessage.details.affectedItems[0]?.zone?.toString()}`;
					const warning = new eXpress.GeneralWarning(
						"AutoSizeValidationWarning",
						"AutoSizeValidation",
						warningMessage,
					);
					client.logErrorAsync(warning, false);
					return Promise.resolve(undefined); // Remove message
				}
				default: {
					return eXpress.utils.exhaustiveFail(validateMessage);
				}
			}
		});
	}, [
		createErrorHandler,
		editor.selection,
		localizedContent,
		messages,
		googleAnalytics,
		showDetails,
		client,
	]);

	useEffect(() => {
		resolveIfMounted(Promise.all(warningItemsPromises))
			.then((warningData) => {
				// Remove undefined warnings and warnings without message
				const finalWarnings = warningData.filter((w) => !!w && w.message) as WarningData[];

				// Add to each click handler a notifiction for the parent
				if (onClick) {
					// Notify container
					finalWarnings.forEach((warning) => {
						if (warning.onClick) {
							const onWarningClick = warning.onClick;
							warning.onClick = () => {
								onWarningClick(); // Perform warning action
								onClick(); // Notify container
							};
						}
					});
				}

				setWarningItems(finalWarnings);
			})
			.catch(createErrorHandler());
	}, [createErrorHandler, onClick, resolveIfMounted, warningItemsPromises]);

	if (!warningItems) return null;

	return <ProductValidation warnings={warningItems} />;
};
ProductValidationContainer.displayName = "ProductValidationContainer";
